import type { JSX, ReactNode } from 'react'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '../components/ui/sheet.tsx'
import { cn } from '../lib/cn.ts'

export interface NavSheetProps {
  'data-testid': string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Names the dialog for assistive technology; visually hidden. */
  title: string
  children: ReactNode
  className?: string
  /** Restore focus to an external trigger when the sheet closes. */
  onCloseAutoFocus?: (event: Event) => void
}

/**
 * NavSheet — an off-canvas panel for navigation at narrow widths.
 *
 * Wraps shadcn's Sheet so feature code does not import
 * `src/components/ui/` directly, and so the accessible name is not optional:
 * a dialog without one is announced as "dialog" and nothing else.
 *
 * @param props - NavSheet props.
 * @param props.open - Whether the panel is showing.
 * @param props.onOpenChange - Called when the panel opens or closes.
 * @param props.title - Accessible name; visually hidden.
 * @param props.children - Panel content.
 * @param props.className - Extra classes.
 * @returns The rendered panel.
 */
export function NavSheet({
  'data-testid': testId,
  open,
  onOpenChange,
  title,
  children,
  className,
  onCloseAutoFocus
}: NavSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        data-testid={testId}
        onCloseAutoFocus={onCloseAutoFocus}
        // Two things here are load-bearing, both about shadcn's built-in
        // close button, which is the only direct-child button.
        // text-sidebar-foreground: it inherits the text colour, and on this
        // dark surface the page default would render it invisible.
        // [&>button]:focus:ring-sidebar-ring: it carries focus:ring-ring, and
        // the house ring is the brand magenta -- a light-surface ring at
        // 2.95:1 on navy, under the 3:1 floor, against 12.35:1 for the
        // sidebar's own. EI-295.
        className={cn(
          'bg-sidebar text-sidebar-foreground [&>button]:focus:ring-sidebar-ring w-64 border-0 p-0',
          className
        )}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  )
}
